import { Button, FieldText } from '@dixa/lib-shared-habla';
import { useCallback, useRef } from 'react';
import {
  Field,
  FieldArray,
  FieldArrayMetaProps,
  WrappedFieldArrayProps,
} from 'redux-form';

import { FieldRepeater, RepeaterDivider } from 'fable/fieldRepeater';
import { useScrollToLastChild } from 'hooks/useScrollToLastChild';

import type { Payload, RulePayload } from '../../nodeTypes/locations';

import { PhoneNumber } from './phoneNumber';
import styles from './rules.module.scss';

export const MATCHINGS_FIELD = 'matchings';
export const NAME_FIELD = 'name';

interface FieldArrayProps<TFieldValue>
  extends WrappedFieldArrayProps<TFieldValue> {
  meta: {
    touched: boolean;
  } & FieldArrayMetaProps;
  rules: Payload['rules'];
}

export function Rules({
  fields,
  rules,
}: FieldArrayProps<RulePayload>): JSX.Element {
  const locationsDiv = useRef<HTMLDivElement>(null);

  useScrollToLastChild<Payload['rules']>(locationsDiv, rules);

  const handleAddNewLocation = useCallback(() => {
    const INIT_LOCATION_FIELD: RulePayload = {
      matchings: [{ type: 'full-number', rule: 'prefix', match: '' }],
      name: '',
      rule_type: 'any',
    };

    fields.push(INIT_LOCATION_FIELD);
  }, [fields, rules]);

  const handleLocationsRemove = useCallback(
    (index: number) => {
      const { matchings } = rules[index];

      if (matchings.length === 1) {
        fields.remove(index);
      }
    },
    [fields, rules],
  );

  const isOnlyNumber = rules.length === 1 && rules[0].matchings.length === 1;

  return (
    <>
      <div className={styles.locations} ref={locationsDiv}>
        {fields.map((field: string, index: number) => (
          <div className={styles.location} key={`${field}-${index}`}>
            <div className={styles.header}>Locations #{index + 1}</div>
            <Field
              className={styles.name}
              component={FieldText}
              helpIcon={{
                text: 'A name will make it easier for you to identify which country codes and/or phone numbers are connected to specific queues (e.g. the London office, Germany, the East Coast, Tuscany, etc.).',
              }}
              label="Name"
              name={`${field}.${NAME_FIELD}`}
              placeholder="Location name (e.g. London or Germany)"
            />
            <FieldArray
              buttonProps={{ placement: 'bottom', label: 'Add phone number' }}
              className={styles.phoneNumbers}
              component={FieldRepeater}
              defaultRowProps={{
                type: 'full-number',
                rule: 'prefix',
                match: '',
              }}
              divider={<RepeaterDivider />}
              rowProps={{
                isOnlyNumber,
                onPhoneNumberRemove: () => handleLocationsRemove(index),
              }}
              key={index}
              name={`${field}.${MATCHINGS_FIELD}`}
              rowComponent={PhoneNumber}
              style={{ maxHeight: 'none', paddingTop: 0 }}
              withBackground={false}
            />
          </div>
        ))}
      </div>
      <Button
        className={styles.button}
        onClick={handleAddNewLocation}
        type="button"
        variant="secondary"
      >
        Add new location
      </Button>
    </>
  );
}
